/**
 * Sessie — wie is er ingelogd, en met welke rol.
 *
 * De rol kwam tot nu toe uit localStorage. Daardoor had een `update` op
 * `public.profiles` geen enkel effect op wat de app liet zien: de app keek daar
 * simpelweg nooit naar. Vanaf hier is de profielrij de enige bron.
 *
 * Bewust een module en geen React-hook: de dataLayer moet dit ook kunnen lezen
 * en die is framework-vrij.
 */

import { supabase } from './client'
import { error as logError } from '../../utils/logger'

/**
 * Loopt authenticatie via Supabase?
 *
 * Voorwaarden waren: migraties 0001 t/m 0008 uitgevoerd en geverifieerd,
 * `anon` met nul rechten (aangetoond), en anonieme signups uit.
 * Op `false` valt de app terug op de legacy-auth in `accounts/logic/authService`.
 */
export const SUPABASE_AUTH_ENABLED = true

/** Demo-accounts bestaan alleen lokaal en raken de database nooit. */
export function isDemoAccountId(accountId: string | undefined): boolean {
  return accountId ? accountId.startsWith('demo') : false
}

export type ProfileRole = 'learner' | 'parent' | 'teacher' | 'admin'
export type DataController = 'school' | 'self'
export type ConsentStatus = 'local' | 'pending' | 'granted' | 'withdrawn'

/**
 * Het profiel zoals de app het nodig heeft. 
 *
 * Bewust géén credential-velden (zoals `recovery_code_hash`): die horen nergens
 * in het geheugen van de client thuis.
 */
export interface SessionProfile {
  id: string
  role: ProfileRole
  displayName: string | null
  grade: number | null
  username: string | null
  dataController: DataController
  consentStatus: ConsentStatus
  /**
   * De koppelcode waarmee een ouder dit kind aan zich verbindt.
   *
   * Hoort bij het profiel op de SERVER, niet bij de lokale accountstaat. Werd
   * die niet meegenomen in de projectie, dan verdween de code uit de
   * instellingen zodra auth via Supabase liep — en daarmee de enige route om
   * een solo-kind aan een ouder te koppelen.
   */
  linkCode: string | null
  /**
   * Het e-mailadres van de ingelogde gebruiker.
   *
   * Staat in `auth.users`, NIET in `profiles` — bewust, want een adres is een
   * inloggegeven en hoort niet in een tabel die anderen mogen lezen. Werd het
   * niet uit de sessie meegenomen, dan toonde het ouderportaal "niet
   * ingesteld" terwijl er gewoon mee was ingelogd.
   *
   * Bij leerlingen is dit het synthetische `.invalid`-adres; dat tonen we
   * nergens, want het is geen echt adres.
   */
  email: string | null
}

export type SessionState =
  /** Nog aan het herstellen bij het opstarten. Kort, maar echt bestaand. */
  | { status: 'loading' }
  /** Geen sessie — de app draait lokaal, zoals vroeger. */
  | { status: 'anonymous' }
  | { status: 'authenticated'; profile: SessionProfile }
  /**
   * Ingelogd, maar geen profielrij (mislukte signup-trigger). Bewust een
   * APARTE toestand: dit als 'anonymous' behandelen zou een inlogscherm tonen
   * aan iemand die al is ingelogd — een onoplosbare lus.
   */
  | { status: 'incomplete'; userId: string }

let current: SessionState = { status: 'loading' }
const listeners = new Set<(s: SessionState) => void>()

function setState(next: SessionState): void {
  current = next
  for (const l of listeners) l(next)
}

/** Huidige staat, synchroon. Voor de dataLayer, die niet kan wachten. */
export function getSessionState(): SessionState {
  return current
}

export function getActiveProfile(): SessionProfile | null {
  return current.status === 'authenticated' ? current.profile : null
}

/** Abonneer op sessiewijzigingen. Retourneert een unsubscribe-functie. */
export function subscribeToSession(
  listener: (s: SessionState) => void,
): () => void {
  listeners.add(listener)
  listener(current)
  return () => {
    listeners.delete(listener)
  }
}

async function loadProfile(
  userId: string,
  email?: string | null,
): Promise<void> {
  const { data, error } = await supabase
    .from('profiles')
    .select(
      'id, role, display_name, grade, username, data_controller, consent_status, link_code',
    )
    .eq('id', userId)
    .maybeSingle()

  if (error) {
    logError('[Session] Profiel laden mislukt', error)
    // Een netwerkfout is iets anders dan een ontbrekend profiel. We weten het
    // niet zeker, dus behandelen we het als incompleet in plaats van iemand uit
    // te loggen — uitloggen bij een haperend netwerk is vijandig gedrag.
    setState({ status: 'incomplete', userId })
    return
  }
  if (!data) {
    setState({ status: 'incomplete', userId })
    return
  }

  setState({
    status: 'authenticated',
    profile: {
      id: data.id as string,
      role: data.role as ProfileRole,
      displayName: (data.display_name as string | null) ?? null,
      grade: (data.grade as number | null) ?? null,
      username: (data.username as string | null) ?? null,
      dataController: (data.data_controller as DataController) ?? 'self',
      consentStatus: (data.consent_status as ConsentStatus) ?? 'local',
      linkCode: (data.link_code as string | null) ?? null,
      // Synthetische leerlingadressen tonen we nergens — dat is geen echt
      // adres en zou alleen maar verwarren.
      email:
        email && !email.endsWith('.invalid') ? email : null,
    },
  })
}

/**
 * Herlaad het profiel van de huidige sessie.
 *
 * Direct na inloggen aangeroepen: zonder deze await navigeert de app voordat de
 * sessie het profiel kent, en draait de routing te vroeg.
 */
export async function refreshProfile(): Promise<void> {
  const { data } = await supabase.auth.getSession()
  if (!data.session?.user) {
    setState({ status: 'anonymous' })
    return
  }
  await loadProfile(data.session.user.id, data.session.user.email)
}

let initialised = false

/** Start sessieherstel en abonneer op auth-wijzigingen. Idempotent. */
export function initSessionContext(): void {
  if (initialised) return
  initialised = true

  void (async () => {
    try {
      const { data } = await supabase.auth.getSession()
      if (data.session?.user)
        await loadProfile(data.session.user.id, data.session.user.email)
      else setState({ status: 'anonymous' })
    } catch (err) {
      // Zonder dit vangnet blijft de app eeuwig op 'loading' en ziet de
      // gebruiker alleen een spinner.
      logError('[Session] Sessieherstel mislukt', err)
      setState({ status: 'anonymous' })
    }
  })()

  supabase.auth.onAuthStateChange((event, session) => {
    // Een verlopen of ingetrokken refresh-token levert geen sessie meer op.
    // Dat is geen fout maar het normale einde van een sessie: netjes naar
    // 'anonymous', zodat de app het inlogscherm toont in plaats van te blijven
    // hangen met een token dat nergens meer geldig is.
    if (event === 'SIGNED_OUT' || !session?.user) {
      setState({ status: 'anonymous' })
      return
    }
    // TOKEN_REFRESHED levert dezelfde gebruiker; opnieuw ophalen is onnodig.
    if (event === 'TOKEN_REFRESHED' && current.status === 'authenticated') return
    void loadProfile(session.user.id, session.user.email)
  })
}

/**
 * Mag er voor dit profiel naar de server worden geschreven?
 *
 * Bij twijfel NEE. Die asymmetrie is bewust: onterecht lokaal opslaan kost
 * hooguit synchronisatie, onterecht naar de server schrijven verwerkt
 * kinderdata zonder grondslag — en dat is niet terug te draaien.=
 */
/**
 * Wie kan de voortgang van de ingelogde LEERLING zien?
 *
 * Bewust een aparte query in plaats van afgeleide lokale staat: een kind moet
 * kunnen zien wie er meekijkt, ook op een apparaat waar die ouder nooit heeft
 * ingelogd. Transparantie over wie toegang heeft is een AVG-eis, geen extraatje
 * — en afgeleide lokale staat kan dat simpelweg niet garanderen.
 */
export interface LinkedPerson {
  id: string
  name: string
  role: ProfileRole
  /** Vanuit de ingelogde gebruiker gezien. */
  relation: 'parent' | 'teacher' | 'child'
  consentStatus: ConsentStatus
  /**
   * Wie gaf de toestemming? Toestemming hoort bij het KIND, niet bij één
   * ouder — zonder deze velden kan de UI niet onderscheiden tussen "ik gaf
   * toestemming" en "de andere ouder gaf toestemming al".
   */
  consentGrantedBy: string | null
  consentGrantedByName: string | null
  consentGrantedAt: string | null
  /** Aantal gekoppelde ouders. Bepaalt of ontkoppelen de laatste is. */
  parentCount: number
}

export async function fetchLinkedPeople(): Promise<LinkedPerson[]> {
  const { data, error } = await supabase.rpc('get_linked_people')
  if (error) {
    logError('[Session] Gekoppelde personen ophalen mislukt', error)
    return []
  }
  return (data ?? []).map((r: Record<string, unknown>) => ({
    id: r.id as string,
    name: (r.name as string | null) ?? 'Onbekend',
    role: r.role as ProfileRole,
    relation: r.relation as LinkedPerson['relation'],
    consentStatus: (r.consent_status as ConsentStatus) ?? 'local',
    consentGrantedBy: (r.consent_granted_by as string | null) ?? null,
    consentGrantedByName:
      (r.consent_granted_by_name as string | null) ?? null,
    consentGrantedAt: (r.consent_granted_at as string | null) ?? null,
    parentCount: (r.parent_count as number | null) ?? 0,
  }))
}

export function shouldUseBackend(): boolean {
  const profile = getActiveProfile()
  if (!profile) return false
  // Schoolroute: de school is verwerkingsverantwoordelijke en heeft een eigen
  // grondslag.
  if (profile.dataController === 'school') return true
  // Soloroute: wij zijn verantwoordelijke, dus er moet aantoonbare ouderlijke
  // toestemming zijn. 'pending' telt NIET — uitgenodigd is niet gegeven.
  return profile.consentStatus === 'granted'
}

/**
 * Vorm van de accountstaat, structureel getypeerd.
 *
 * Bewust GEEN import uit `../../accounts`: die barrel trekt het hele
 * accounts-domein mee, terwijl deze module ook door de dataLayer gelezen moet
 * kunnen worden — en die is framework- en domeinvrij.
 */
export interface AccountsLike {
  accountsById: Record<string, Record<string, unknown>>
  activeAccountId: string | null
  [key: string]: unknown
}

/**
 * Projecteer het ingelogde profiel als ACTIEF account in de accountstaat.
 *
 * `AuthGuard`, `PortalSwitch` en alle portalen lezen de actieve gebruiker uit
 * `multiAccountState.accounts`. Zonder deze projectie logt iemand in bij
 * Supabase terwijl de app een oud localStorage-account actief houdt — dan zie
 * je de naam van een demo-leerling terwijl je als iemand anders bent ingelogd.
 *
 * Expliciet TIJDELIJK: zodra de portalen de sessie rechtstreeks lezen, kan dit
 * weg. Er wordt géén `passwordHash` gezet — de server verifieert het wachtwoord
 * en een hash hoort niet in de clientstaat thuis.
 */
export function applyProfileToAccounts(
  accounts: AccountsLike,
  profile: SessionProfile,
): AccountsLike {
  const now = Date.now()
  const existing = accounts.accountsById[profile.id]

  // Lokale extra's (groep, onboarding-voortgang) blijven behouden; de
  // identiteitsvelden en de ROL komen van de server.
  const merged: Record<string, unknown> = {
    ...(existing ?? {}),
    id: profile.id,
    role: profile.role,
    displayName: profile.displayName ?? 'Gebruiker',
    createdAt: (existing?.createdAt as number | undefined) ?? now,
    lastActive: now,
    hasCompletedOnboarding: true,
  }
  if (profile.username) merged.username = profile.username
  if (profile.grade != null) merged.grade = profile.grade
  if (profile.linkCode) merged.linkCode = profile.linkCode
  if (profile.email) merged.email = profile.email

  return {
    ...accounts,
    accountsById: { ...accounts.accountsById, [profile.id]: merged },
    activeAccountId: profile.id,
  }
}
